import {
    BASE_VALUE,
    BulkSelectionContextValue,
} from '@/components/bulkQueue/tables/BulkSelectionContext';
import {
    Entity,
    useBulkSelectionStore,
    useBulkState,
    useSelectedEntityIdsKey,
} from '@/components/bulkQueue/tables/bulkContextUtils';
import { BULK_CLINICIAN_ASSIGNMENT_TABLE_QUERY } from '@/components/bulkQueue/tables/clinician/BulkClinicianAssignmentTable';
import {
    BulkUpdateClinicianVisitAssignmentMutation,
    BulkUpdateClinicianVisitAssignmentMutationVariables,
} from '@/components/bulkQueue/tables/clinician/BulkClinicianContext.gql.types';
import {
    UseAssignmentClinicianRecsResult,
    useAssignmentClinicianRecs,
} from '@/components/decisionSupport/hooks/useAssignmentClinicianRecs';
import {
    DecisionSupportSelection,
    canSelectVisit,
    pickTopRanked,
} from '@/components/decisionSupport/utils/decisionSupport.utils';
import { VisitOverrideState } from '@/components/decisionSupport/utils/visitAssignmentInput.utils';
import { ALL_VISIT_COUNTS } from '@/pages/AllVisitsPage/AllVisits.gql';
import { onError } from '@/toast';
import {
    ProviderWritableStore,
    createDefaultWritableProviderStore,
    createStore,
    useStoreValue,
} from '@/utils/store';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { UpdateClinicianAppointmentClinicianAssignmentInput } from 'gql-types';
import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

const BULK_UPDATE_CLINICIAN_VISIT_ASSIGNMENT = gql`
    mutation BulkUpdateClinicianVisitAssignment(
        $input: BulkUpdateClinicianAppointmentClinicianAssignmentInput!
    ) {
        bulkUpdateClinicianAppointmentClinicianAssignment(input: $input) {
            success
            successCount
            errorCount
        }
    }
`;

type AssignmentClinicianResult =
    UseAssignmentClinicianRecsResult['resultsByVisitId'] extends ReadonlyMap<string, infer V>
        ? V
        : never;
export type AssignmentClinicianRecommendation =
    AssignmentClinicianResult['clinicianRecommendation'];
type AssignmentDefinitions = UseAssignmentClinicianRecsResult['definitions'];

// Whether the user has explicitly picked a clinician for at least one of a visit's appointments
const hasUserPick = (
    selection: Map<string, DecisionSupportSelection | null> | null | undefined,
): boolean => (selection ? Array.from(selection.values()).some((s) => s != null) : false);

type BulkClinicianContextValue = BulkSelectionContextValue<{
    selectedRecommendationStore: ProviderWritableStore<
        Map<string, DecisionSupportSelection | null>
    >;
    // DS visitAssignment flow. The cells read clinician recommendations from
    // these; selection/submit flow through the shared `selectedRecommendationStore`.
    assignmentDefinitions: AssignmentDefinitions;
    assignmentResultsByVisitId: UseAssignmentClinicianRecsResult['resultsByVisitId'];
    assignmentSelectableIds: string[];
}>;

const DEFAULT_VALUE: BulkClinicianContextValue = {
    ...BASE_VALUE,
    selectedRecommendationStore: createDefaultWritableProviderStore(new Map()),
    assignmentDefinitions: null,
    assignmentResultsByVisitId: new Map(),
    assignmentSelectableIds: [],
};

const BulkClinicianContext = createContext<BulkClinicianContextValue>(DEFAULT_VALUE);

type BulkClinicianContextProviderProps = {
    children: ReactNode;
    entities: Entity[];
};

export const BulkClinicianContextProvider = (props: BulkClinicianContextProviderProps) => {
    const { children, entities } = props;
    const [selectionStore, setValue, selectedCount, resetSelection, pruneToEntityIds] =
        useBulkSelectionStore();
    const selectedClinicianStoreRef = useRef(
        createStore<Map<string, DecisionSupportSelection | null>>(),
    );
    const selectedClinicianStore = selectedClinicianStoreRef.current;

    // Prune selections when the displayed entities change (e.g. filter change).
    useEffect(() => {
        const entityIds = new Set(entities.map((e) => e.id));
        pruneToEntityIds(entityIds);
    }, [entities, pruneToEntityIds]);

    // DS visitAssignment flow (visits only). Clinician recommendations choose
    // the clinician (no clinician override sent) with the date held implicitly.
    const visitOverrideStates = useMemo<VisitOverrideState[]>(
        () =>
            entities
                .filter((entity) => entity.__typename === 'Visit')
                .map((entity) => ({
                    visitId: entity.id,
                    appointments: [],
                    dateOverride: undefined,
                })),
        [entities],
    );
    const {
        definitions: assignmentDefinitions,
        resultsByVisitId: assignmentResultsByVisitId,
        loading: assignmentLoading,
    } = useAssignmentClinicianRecs(visitOverrideStates);

    // The store holds only the user's picks.
    // the recommended default is derived at read/submit (see getDefaultClinician)
    useEffect(() => {
        selectedClinicianStore.setAll(new Map());
    }, [assignmentResultsByVisitId, selectedClinicianStore]);

    const getDefaultClinician = useCallback(
        (entityId: string): DecisionSupportSelection | null => {
            const recs =
                assignmentResultsByVisitId.get(entityId)?.clinicianRecommendation.recommendations;
            const top = pickTopRanked(recs);
            return top ? { id: top.clinician.id, label: top.clinician.fullName } : null;
        },
        [assignmentResultsByVisitId],
    );

    // Recomputes the selectable set (Select All + header count) when the user picks;
    // the selection store mutates outside React (see useSelectedEntityIdsKey).
    const pickedVisitIdsKey = useSelectedEntityIdsKey(selectedClinicianStore, hasUserPick);

    // A visit is selectable when it has a recommended default OR an explicit pick
    const assignmentSelectableIds = useMemo<string[]>(() => {
        const pickedVisitIds = new Set(pickedVisitIdsKey ? pickedVisitIdsKey.split(',') : []);
        return entities
            .filter((entity) => entity.__typename === 'Visit')
            .filter((entity) =>
                canSelectVisit(
                    (assignmentResultsByVisitId.get(entity.id)?.clinicianRecommendation
                        .recommendations.length ?? 0) > 0,
                    pickedVisitIds.has(entity.id),
                ),
            )
            .map((entity) => entity.id);
    }, [entities, assignmentResultsByVisitId, pickedVisitIdsKey]);

    const [mutateVisits, { loading: submittingVisit }] = useMutation<
        BulkUpdateClinicianVisitAssignmentMutation,
        BulkUpdateClinicianVisitAssignmentMutationVariables
    >(BULK_UPDATE_CLINICIAN_VISIT_ASSIGNMENT, { onError });

    const submit = useCallback(async () => {
        const selected = selectionStore.getAll();
        const visitAppointments: UpdateClinicianAppointmentClinicianAssignmentInput[] = [];

        selected.forEach((isSelected, entityId) => {
            if (!isSelected) {
                return;
            }
            const entity = entities.find((e) => e.id === entityId);
            const stored = selectedClinicianStore.getSnapshot(entityId);

            // Derive pick ?? top clinician per appointment.
            (entity?.appointments ?? []).forEach((appointment) => {
                const selection = stored?.get(appointment.id) ?? getDefaultClinician(entityId);
                if (!selection?.id) {
                    return;
                }
                visitAppointments.push({
                    clinicianAppointmentId: appointment.id,
                    clinicianId: selection.id,
                });
            });
        });

        if (visitAppointments.length > 0) {
            await mutateVisits({
                variables: { input: { clinicianAppointments: visitAppointments } },
                refetchQueries: [ALL_VISIT_COUNTS, BULK_CLINICIAN_ASSIGNMENT_TABLE_QUERY],
                awaitRefetchQueries: true,
            });
        }
        resetSelection();
    }, [
        mutateVisits,
        selectionStore,
        selectedClinicianStore,
        getDefaultClinician,
        entities,
        resetSelection,
    ]);

    const state = useBulkState(assignmentLoading, submittingVisit);

    const value: BulkClinicianContextValue = {
        isEntitySelectedStore: {
            ...selectionStore,
            setValue,
        },
        selectedRecommendationStore: selectedClinicianStore,
        submit,
        state,
        selectedCount,
        entityCount: entities.length,
        assignmentDefinitions,
        assignmentResultsByVisitId,
        assignmentSelectableIds,
    };

    return <BulkClinicianContext.Provider value={value}>{children}</BulkClinicianContext.Provider>;
};

export const useBulkClinicianContext = () => {
    const {
        submit,
        state,
        selectedCount,
        entityCount,
        isEntitySelectedStore,
        assignmentSelectableIds,
    } = useContext(BulkClinicianContext);
    const summary = useClinicianSummary();

    const selectableCount = assignmentSelectableIds.length;

    const selectAll = useCallback(() => {
        const currentlySelected = isEntitySelectedStore.getAll();
        const allSelected =
            assignmentSelectableIds.length > 0 &&
            assignmentSelectableIds.every((id) => currentlySelected.get(id) === true);
        assignmentSelectableIds.forEach((id) => {
            isEntitySelectedStore.setValue?.(id, !allSelected);
        });
    }, [assignmentSelectableIds, isEntitySelectedStore]);

    return useMemo(
        () => ({
            submit,
            state,
            selectedCount,
            entityCount,
            summary,
            selectableCount,
            selectAll,
        }),
        [selectedCount, summary, entityCount, state, submit, selectableCount, selectAll],
    );
};

export const useClinicianSummary = () => {
    const { selectedRecommendationStore, isEntitySelectedStore } = useContext(BulkClinicianContext);

    return useSyncExternalStore(
        (cb: () => void) => {
            const unsub1 = selectedRecommendationStore.subscribeToAll(cb);
            const unsub2 = isEntitySelectedStore.subscribeToAll(cb);
            return () => {
                unsub1();
                unsub2();
            };
        },
        () => {
            const clinicianIds = new Set<string>();
            const clinicians: DecisionSupportSelection[] = [];
            selectedRecommendationStore.getAll().forEach((appointmentMap) => {
                appointmentMap.forEach((selection) => {
                    if (selection && !clinicianIds.has(selection.id)) {
                        clinicianIds.add(selection.id);
                        clinicians.push(selection);
                    }
                });
            });
            const list = clinicians.map((c) => c.label).join(', ');
            const label = `clinician${clinicians.length > 1 ? 's' : ''}`;
            const numSelected = Array.from(isEntitySelectedStore.getAll().values()).filter(
                Boolean,
            ).length;
            return `Assign ${label} to ${numSelected} visits: ${list}`;
        },
    );
};

export const useIsClinicianSelected = (
    entityId: string,
): [boolean, (isSelected: boolean) => void] => {
    const { isEntitySelectedStore } = useContext(BulkClinicianContext);
    const isSelected = useStoreValue(BulkClinicianContext, 'isEntitySelectedStore', entityId);

    const setIsSelected = useCallback(
        (selected: boolean) => {
            isEntitySelectedStore.setValue?.(entityId, selected);
        },
        [isEntitySelectedStore, entityId],
    );

    return [isSelected, setIsSelected];
};

export const useCanSelectClinicianVisit = (entityId: string): boolean => {
    const { assignmentResultsByVisitId } = useContext(BulkClinicianContext);
    // Subscribe to the user's picks so the checkbox appears once a clinician is
    // chosen for a visit that has no recommended default (e.g. incompatible-only).
    const selectedMap = useStoreValue(
        BulkClinicianContext,
        'selectedRecommendationStore',
        entityId,
    );

    const hasRecommendation =
        (assignmentResultsByVisitId.get(entityId)?.clinicianRecommendation.recommendations.length ??
            0) > 0;
    return canSelectVisit(hasRecommendation, hasUserPick(selectedMap));
};

// Per-visit clinician recommendation + shared definitions for the new flow.
export const useAssignmentClinicianRec = (
    entityId: string,
): {
    definitions: AssignmentDefinitions;
    clinicianRecommendation: AssignmentClinicianRecommendation | null;
} => {
    const { assignmentDefinitions, assignmentResultsByVisitId } = useContext(BulkClinicianContext);
    return {
        definitions: assignmentDefinitions,
        clinicianRecommendation:
            assignmentResultsByVisitId.get(entityId)?.clinicianRecommendation ?? null,
    };
};

export const useSelectedClinician = (
    entityId: string,
    appointmentId: string,
): [DecisionSupportSelection | null, (selection: DecisionSupportSelection | null) => void] => {
    const { selectedRecommendationStore, isEntitySelectedStore } = useContext(BulkClinicianContext);
    const selectedCliniciansMap = useStoreValue(
        BulkClinicianContext,
        'selectedRecommendationStore',
        entityId,
    );

    const selectedClinician = selectedCliniciansMap
        ? selectedCliniciansMap.get(appointmentId) ?? null
        : null;

    const setSelectedClinician = useCallback(
        (selection: DecisionSupportSelection | null) => {
            const current = selectedRecommendationStore.getSnapshot(entityId);
            const next = new Map(current ?? []);
            next.set(appointmentId, selection);
            selectedRecommendationStore.setValue(entityId, next);
            // Auto-select the entity when a clinician is chosen (mirrors useSelectedDate).
            if (selection) {
                isEntitySelectedStore.setValue?.(entityId, true);
            }
        },
        [selectedRecommendationStore, isEntitySelectedStore, entityId, appointmentId],
    );

    return [selectedClinician, setSelectedClinician];
};
